/**
 * ClientDemoPage (`/demos/:slug`)
 *
 * Blank canvas rendered after the operator unlocks the demo. We'll fill
 * each slug's canvas in a follow-up prompt — this file deliberately
 * only holds the gate + placeholder. If the sessionStorage flag isn't
 * set we bounce back to the unlock page.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import { ArrowLeft, Cpu } from "lucide-react";
import DemoTopBar from "@/components/DemoTopBar";
import ExchangeAlliancePage from "@/pages/clientDemos/ExchangeAlliancePage";

// Each template key renders its own engine page.
const TEMPLATE_PAGES = { exchange: ExchangeAlliancePage };

export default function ClientDemoPage() {
  const { slug } = useParams();
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(`demo_unlock_${slug}`) === "1"; } catch { return false; }
  });
  const [businessName, setBusinessName] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [templated, setTemplated] = useState(null);
  useEffect(() => {
    axios.get(`${API}/demos/${slug}/config`).then(({ data }) => setTemplated(data.template || "exchange")).catch(() => setTemplated(false));
  }, [slug]);
  useEffect(() => {
    axios.get(`${API}/demos/${slug}/intro`).then(({ data }) => setLogoUrl(data.logo_url)).catch(() => {});
  }, [slug]);

  // Grab the display name from the public directory so the page title
  // isn't a raw slug. Falls back silently if the fetch fails.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/demo-directory`);
        const hit = (data?.items || []).find((x) => x.slug === slug);
        if (alive && hit) setBusinessName(hit.business_name);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (!unlocked) {
    return <Navigate to={`/demos/${slug}/unlock`} replace />;
  }

  if (templated === null) {
    return <section data-testid="client-demo-page" className="min-h-screen bg-[#0A192F]" />;
  }
  const ClientCanvas = templated ? TEMPLATE_PAGES[templated] : null;

  return (
    <section
      data-testid="client-demo-page"
      data-demo-slug={slug}
      className={`min-h-screen text-slate-100 ${templated === "exchange" ? "tea-theme bg-[#0A0612]" : "bg-[#0A192F]"}`}
    >
      <DemoTopBar />
      <div className="py-10 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-8">
          <Link
            to="/demos"
            data-testid="client-demo-back"
            className="inline-flex items-center gap-1.5 text-slate-400 hover:text-brand-cyan font-mono text-[11px] tracking-[0.18em] uppercase transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to directory
          </Link>
          <div className="flex items-center gap-3">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-brand-cyan">
              // UNLOCKED
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-8">
          {logoUrl ? (
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-1.5">Client Engine</div>
              <h1 className="sr-only">{businessName || slug}</h1>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                alt={businessName || slug}
                data-testid="client-demo-brand-logo"
                className="h-10 sm:h-12 w-auto max-w-[80vw] object-contain drop-shadow-[0_0_14px_rgba(6,182,212,0.25)] animate-in fade-in duration-500"
              />
            </div>
          ) : (
            <>
            <div data-testid="client-demo-brand-chip" className="w-11 h-11 rounded-xl bg-gradient-to-tr from-brand-blue via-brand-cyan to-brand-emerald flex items-center justify-center">
              <Cpu className="w-6 h-6 text-navy-950" />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-slate-500">Client Engine</div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
                {businessName || slug}
              </h1>
            </div>
            </>
          )}
        </div>

        {ClientCanvas ? (
          <ClientCanvas businessName={businessName || slug} slug={slug} />
        ) : (
          <div
            data-testid="client-demo-canvas"
            className="mt-6 rounded-2xl border border-white/10 bg-navy-900/60 backdrop-blur-md p-10 min-h-[400px] flex items-center justify-center text-center"
          >
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-brand-cyan mb-3">
                // BLANK CANVAS
              </div>
              <p className="text-slate-300 text-lg max-w-xl">
                This demo canvas will be built out for{" "}
                <span className="text-white font-semibold">{businessName || slug}</span>{" "}
                in the next prompt.
              </p>
              <p className="text-slate-500 text-sm mt-3">
                The directory → unlock → demo flow is wired and ready.
              </p>
            </div>
          </div>
        )}
      </div>
      </div>
    </section>
  );
}
